import { useEffect, useRef } from 'react';

/**
 * „Vissza-szándék" verem — az Android vissza gombhoz.
 *
 * A PROBLÉMA. A vissza gomb alapból a WebView előzményeit lépkedi, a
 * megnyitott lapok (sheet, lightbox, megerősítő doboz) viszont NEM
 * előzmények, hanem React-állapot. Enélkül a nyitott hozzászólás-lap mellett
 * a vissza gomb elnavigált volna a képernyőről — a lap meg ott maradt volna
 * a következő megnyitásig.
 *
 * A MEGOLDÁS egy LIFO-verem: minden megnyitott réteg feliratkozik, és a
 * LEGUTOLJÁRA nyitott kapja meg a megnyomást. Ez pontosan az a sorrend,
 * amit a felhasználó lát: ami legfelül van, az záródik először.
 *
 * ⚠️ A VEREM MODULSZINTŰ, NEM CONTEXT. Így olyan komponens is fel tud
 * iratkozni, amelyik egy portálon keresztül a React-fán kívülre rendel
 * (a Mapbox-buborékba rendelt tulajdonos-kártya pontosan ilyen), és nem
 * kell minden réteget egy közös szolgáltató alá kényszeríteni.
 */

type BackHandler = () => void;

const stack: BackHandler[] = [];

/**
 * A legfelső feliratkozó lefuttatása.
 *
 * @returns `true`, ha volt kinek átadni — ilyenkor a hívónak NEM szabad
 *   navigálnia, mert a megnyomást egy réteg bezárása fogyasztotta el.
 */
export function runTopBackHandler(): boolean {
  const handler = stack[stack.length - 1];
  if (!handler) return false;
  handler();
  return true;
}

/** Hány réteg várja éppen a vissza gombot. Teszteléshez és hibakereséshez. */
export function openBackHandlerCount(): number {
  return stack.length;
}

/**
 * Feliratkozás React nélkül — a hook is ezt használja.
 *
 * Külön függvény, mert a verem viselkedése (LIFO-sorrend, kivétel a közepéből)
 * így tesztelhető: a projektben nincs React-tesztkörnyezet, tehát egy hookot
 * nem lehetne futtatni a tesztekben, ezt viszont igen.
 *
 * @returns Leiratkozó. Kétszer meghívva nem csinál kárt.
 */
export function pushBackHandler(handler: BackHandler): () => void {
  stack.push(handler);
  return () => {
    const index = stack.lastIndexOf(handler);
    if (index !== -1) stack.splice(index, 1);
  };
}

/**
 * Feliratkozás a vissza gombra, amíg ez a réteg nyitva van.
 *
 * @param active Nyitva van-e. `false`-nál a réteg nincs a veremben — egy
 *   becsukott lap nem nyelheti el a megnyomást.
 * @param handler A bezárás. Mindig a LEGFRISSEBB változat fut le, akkor is,
 *   ha a komponens azóta újrarenderelt (ezért ül refben).
 */
export function useBackHandler(active: boolean, handler: BackHandler): void {
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => {
    if (!active) return undefined;

    // Stabil burkoló: a verembe ez kerül, és a `latest` refen át mindig az
    // aktuális bezárást hívja. Enélkül minden renderben új függvényt kellene
    // ki-be pakolni a veremből, és a sorrend összekeveredne.
    return pushBackHandler(() => latest.current());
  }, [active]);
}
